import type {
  EditorCaseData,
  EditorFieldCatalogEntry,
  EditorFieldParagraph,
  EditorFieldParagraphPiece,
} from '@messenger/lotus'
import {
  EDITOR_FIELD_FINAL_RESTING,
  EDITOR_FIELD_NAME_OF_DECEASED,
  editorCaseServices,
  editorTableRowTypes,
} from '@messenger/lotus'

/**
 * One row of the menu spec: the catalog key it inserts and the English label
 * the menu shows for it, which `editor.textBox.fieldMenu.<key>` translates; a
 * row with no label shows its field's catalog label. `additional` stands for
 * every service or visitation of the case (the key's kind) other than the
 * default one, a row each, in case order.
 */
type FieldMenuSpecRow = {
  key: string
  label?: string
  additional?: true
}

/**
 * A row that inserts several details at once: paragraphs of catalog fields
 * and the literal text between them. `id` names it in the data attribute
 * and in `editor.textBox.fieldMenu.<id>`, which translates `label`.
 */
export type FieldMenuLayout = {
  id: string
  label: string
  paragraphs: readonly EditorFieldParagraph[]
}

/** A heading, its rows and its layouts; a null heading puts them in the list itself. */
type FieldMenuSpecGroup = {
  group: string | null
  rows: readonly FieldMenuSpecRow[]
  layouts?: readonly FieldMenuLayout[]
}

// A grandparent's mother and father show their catalog labels ("Grandmother's
// mother"), which name the generation the column holds.
const genealogySide = (side: 'maternal' | 'paternal'): FieldMenuSpecRow[] => [
  { key: `${side}_grandmother`, label: 'Grandmother' },
  { key: `${side}_grandmothers_mother` },
  { key: `${side}_grandmothers_father` },
  { key: `${side}_grandfather`, label: 'Grandfather' },
  { key: `${side}_grandfathers_mother` },
  { key: `${side}_grandfathers_father` },
]

const funeralHome = (key: string): EditorFieldParagraphPiece => ({
  kind: 'field',
  fieldKey: `fh:${key}`,
})

const COMMA: EditorFieldParagraphPiece = { kind: 'literal', text: ', ' }

/**
 * The funeral home layouts of the TinyMCE dynamic-text templates
 * (`apps/mdl-frontend/src/utils/tinyMCE/caseDetails/handleFuneralHome.js`),
 * one paragraph per line. A full address prints its city, state and ZIP
 * through `fh:city_state_zip`, which joins them by the rule the TinyMCE
 * template uses, so a home with no city or no state prints what TinyMCE
 * prints; separate fields would drop the comma TinyMCE keeps.
 */
const FUNERAL_HOME_LAYOUTS: readonly FieldMenuLayout[] = [
  {
    id: 'funeral_home_name_city_state',
    label: 'Name, city and state',
    paragraphs: [
      [funeralHome('name')],
      [funeralHome('city'), COMMA, funeralHome('state')],
    ],
  },
  {
    id: 'funeral_home_name_full_address',
    label: 'Name and full address',
    paragraphs: [
      [funeralHome('name')],
      [funeralHome('address'), COMMA, funeralHome('city_state_zip')],
    ],
  },
  {
    id: 'funeral_home_name_full_address_email',
    label: 'Name, full address and email',
    paragraphs: [
      [funeralHome('name')],
      [funeralHome('address'), COMMA, funeralHome('city_state_zip')],
      [funeralHome('email')],
    ],
  },
]

/**
 * The Insert detail menu: its headings, rows, layouts, order and labels. A
 * catalog field no row names stays out of the menu and still resolves in
 * every template that carries it.
 */
const FIELD_MENU_SPEC: readonly FieldMenuSpecGroup[] = [
  {
    group: 'Deceased name',
    rows: [
      { key: EDITOR_FIELD_NAME_OF_DECEASED, label: 'Full Name of Deceased' },
      { key: 'name_prefix', label: 'Prefix Name' },
      { key: 'name_first', label: 'First Name' },
      { key: 'name_nickname', label: 'Nickname' },
      { key: 'name_middle', label: 'Middle Name' },
      { key: 'name_maiden', label: 'Maiden' },
      { key: 'name_last', label: 'Last Name' },
      { key: 'name_suffix', label: 'Suffix' },
      { key: 'first_letter_first', label: 'Initial First name' },
      { key: 'first_letter_middle', label: 'Initial middle name' },
      { key: 'first_letter_last', label: 'Initial last name' },
    ],
  },
  {
    group: 'Deceased Info',
    rows: [
      { key: 'date_of_birth', label: 'Date of Birth' },
      { key: 'place_of_birth', label: 'Place of Birth' },
      { key: 'date_of_death', label: 'Date of Death' },
      { key: 'place_of_death', label: 'Place of Death' },
      { key: 'mother_of_deceased', label: 'Mother' },
      { key: 'father_of_deceased', label: 'Father' },
      { key: 'spouse', label: 'Spouse' },
      { key: 'date_of_marriage', label: 'Date of Marriage' },
      { key: 'siblings', label: 'Siblings' },
      { key: 'children', label: 'Children' },
      { key: 'additional_family', label: 'Additional family' },
      { key: 'year_of_birth', label: 'Year of Birth' },
      { key: 'year_of_death', label: 'Year of Death' },
    ],
  },
  { group: "Genealogy - Mother's Side", rows: genealogySide('maternal') },
  { group: "Genealogy - Father's Side", rows: genealogySide('paternal') },
  { group: null, rows: [{ key: 'obituary', label: 'Obituary' }] },
  {
    group: null,
    rows: [
      {
        key: 'family_appreciation_message',
        label: 'Family Appreciation Message',
      },
    ],
  },
  {
    group: 'Service Information',
    rows: [
      { key: 'visitation', label: 'Default Visitation' },
      { key: 'service', label: 'Default Service' },
      { key: 'visitation', additional: true },
      { key: 'service', additional: true },
      { key: 'order_of_service', label: 'Order of Service' },
      { key: 'clergy', label: 'Clergy' },
      { key: 'pallbearers', label: 'Pallbearer' },
      { key: 'honorary_pallbearers', label: 'Honorary Pallbearer' },
      { key: 'organizations', label: 'Organizations' },
      { key: EDITOR_FIELD_FINAL_RESTING, label: 'Interment' },
    ],
  },
  { group: 'Funeral Home Info', rows: [], layouts: FUNERAL_HOME_LAYOUTS },
  {
    group: 'Headers',
    rows: [
      { key: 'birth_header', label: 'Birth Header' },
      { key: 'death_header', label: 'Death Header' },
      { key: 'remembrance_header', label: 'Remembrance Header' },
      { key: 'officiating_header', label: 'Officiating Header' },
      { key: 'memorial_service_header', label: 'Memorial Service Header' },
      { key: 'visitation_header', label: 'Visitation Header' },
      { key: 'final_resting_header', label: 'Final Resting Header' },
      { key: 'pallbearers_header', label: 'Pallbearers Header' },
      {
        key: 'family_appreciation_header',
        label: 'Family Appreciation Header',
      },
    ],
  },
  // A theme whose application is M3 fills the line ("Printed in U.S.A. by
  // Messenger"); every other theme fills it empty, as TinyMCE's Printed in
  // does, so the row is offered on every product.
  { group: null, rows: [{ key: 'printed_in', label: 'Printed in' }] },
]

/** One row of the Insert detail menu: what it inserts, and what labels it. */
export type FieldMenuRow = {
  entry: EditorFieldCatalogEntry
  /** The menu's English label for the row; absent, its field's catalog label. */
  label?: string
  /**
   * The case's service or visitation the row inserts in place of the
   * default one, and its number among its kind's additional rows, from 1.
   */
  service?: { id: string; location: string; number: number }
  /** The additional-family group whose rows the row's table prints. */
  rowType?: string
}

/** A heading of the Insert detail menu, its rows and its layouts; a null heading's stand in the list itself. */
export type FieldMenuGroup = {
  group: string | null
  rows: FieldMenuRow[]
  layouts: FieldMenuLayout[]
}

const serviceKind = (type: string): 'service' | 'visitation' =>
  type === 'visitation' ? 'visitation' : 'service'

const menuRows = (
  spec: FieldMenuSpecRow,
  entry: EditorFieldCatalogEntry,
  caseData: EditorCaseData | undefined,
): FieldMenuRow[] => {
  if (spec.additional)
    return (caseData ? editorCaseServices(caseData) : [])
      .filter(
        (service) =>
          !service.isDefault && serviceKind(service.type) === entry.key,
      )
      .map((service, index) => ({
        entry,
        service: {
          id: service.id,
          location: service.location,
          number: index + 1,
        },
      }))
  const rowTypes = caseData ? editorTableRowTypes(entry.key, caseData) : []
  return rowTypes.length === 0
    ? [{ entry, label: spec.label }]
    : rowTypes.map((rowType) => ({ entry, label: spec.label, rowType }))
}

/**
 * The Insert detail menu over a catalog and, when there is one, the case:
 * the spec's headings, rows and layouts in the spec's order, each labelled
 * by the spec. A row whose key the catalog does not know is left out, as is
 * a layout naming any such key, and a heading left with neither is left out
 * with them, so a renamed catalog key never paints a dead or partial row.
 * The case adds a row per additional visitation and per additional service,
 * and turns Additional family into a row per group of its rows.
 */
export const fieldMenuGroups = (
  catalog: readonly EditorFieldCatalogEntry[],
  caseData?: EditorCaseData,
): FieldMenuGroup[] => {
  const known = new Set(catalog.map((entry) => entry.key))
  return FIELD_MENU_SPEC.flatMap(({ group, rows, layouts = [] }) => {
    const built = rows.flatMap((spec) => {
      const entry = catalog.find((candidate) => candidate.key === spec.key)
      return entry ? menuRows(spec, entry, caseData) : []
    })
    const offered = layouts.filter((layout) =>
      layout.paragraphs.every((pieces) =>
        pieces.every(
          (piece) => piece.kind === 'literal' || known.has(piece.fieldKey),
        ),
      ),
    )
    return built.length === 0 && offered.length === 0
      ? []
      : [{ group, rows: built, layouts: offered }]
  })
}
